import React, { FC, ReactNode } from 'react';
import { Container, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { SpacingLayout } from 'components/atoms';

interface FeatureSectionProps {
  bgImage: string;
  featuresList: Array<{ svgIcon: ReactNode; text: string }>;
}

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    backgroundImage: ({ bgImage }: Partial<FeatureSectionProps>) =>
      `linear-gradient(0deg, rgba(0, 26, 85, 0.5), rgba(0, 26, 85, 0.5)), url(${bgImage})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    zIndex: 2,
    position: 'relative',
  },
  underLine: {
    maxWidth: 193,
    margin: theme.spacing(0, 'auto', 2),
    [theme.breakpoints.up('sm')]: {
      maxWidth: 180,
    },
  },
  imageIcon: {
    marginBottom: theme.spacing(1),
    display: 'inline-flex',
    [theme.breakpoints.up('sm')]: {
      marginBottom: theme.spacing(2),
    },
    '& svg': {
      maxWidth: 81,
      height: 81,
      [theme.breakpoints.up('sm')]: {
        maxWidth: 96,
        height: 96,
      },
    },
  },
  gridItem: {
    textAlign: 'center',
    '&:not(:last-child)': {
      marginBottom: theme.spacing(4),
    },
    [theme.breakpoints.up('sm')]: {
      '&:not(:last-child)': {
        marginBottom: 0,
      },
      '&:not(:nth-last-child(-n+2))': {
        marginBottom: theme.spacing(4),
      },
    },
  },
  text: {
    maxWidth: 340,
    margin: 'auto',
    [theme.breakpoints.up('lg')]: {
      fontSize: 24,
    },
  },
}));

const FeatureSection: FC<FeatureSectionProps> = ({ bgImage, featuresList }) => {
  const classes = useStyles({ bgImage });

  return (
    <>
      {!!featuresList.length && (
        <SpacingLayout className={classes.wrapper}>
          <Container maxWidth={false} fixed>
            <Grid container spacing={6} justify="center" alignItems="flex-start">
              {featuresList.map((feature, index) => (
                <Grid
                  item
                  sm={6}
                  md={4}
                  className={classes.gridItem}
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={(index + 1) * 200}
                >
                  <Grid className={classes.imageIcon} item>
                    {feature.svgIcon}
                  </Grid>
                  <hr className={classes.underLine} />
                  <Typography color="textSecondary" variant="h4" component="p" className={classes.text}>
                    {feature.text}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Container>
        </SpacingLayout>
      )}
    </>
  );
};

export default FeatureSection;
